import { AppContext, AppProps } from 'next/app'
import getConfig from 'next/config'
import styled from 'styled-components'
import {
  ThemeProvider,
  UniversalThirdPartyScripts,
} from '@fe-toolkit/web-components/ssr'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
// fe-common's Brand Refresh header and footer leave font loading to MFE hosts.
// Without this they render in the browser's default serif.
import '@giffgaff/fe-design-system-fonts/fonts.css'

import {
  getServerCookieConsent,
  setServerUserNapaCookies,
} from '@fe-toolkit/utility/node'
import { customEvent } from '@fe-toolkit/eventing'

import { MemberDetails } from '@/api/member/member.types'
import memberApi from '@/api/member/member.api'
import { initSnowplowTracker, trackConsent } from '@/utils/snowplow'
import {
  getHeaderFlags,
  HeaderFlags,
  HeaderFlagsContext,
} from '@/utils/headerFlags'
import { authCookies } from '@/utils/authCookies'

const { publicRuntimeConfig } = getConfig()

// This is to avoid FOUC (Flash of Unstyled Content)
const Wrapper = styled.div`
  display: block !important;
`

type InitialProps = {
  memberDetails?: MemberDetails
  headerFlags: HeaderFlags
  cookieConsent: boolean
  hasTrackingData: boolean
}

if (publicRuntimeConfig.FF_ENABLE_SNOWPLOW) {
  if (typeof window !== 'undefined') {
    initSnowplowTracker({
      collectorUrl: publicRuntimeConfig.SNOWPLOW_COLLECTOR_URL,
      appId: 'web-mfe',
      namespace: 'connect',
      postPath: publicRuntimeConfig.SNOWPLOW_POST_PATH,
    })
  }
}

function MyApp({
  Component,
  pageProps,
  memberDetails,
  headerFlags,
  cookieConsent,
  hasTrackingData,
}: InitialProps & AppProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: 60 * 1000,
          },
        },
      }),
  )

  useEffect(() => {
    // Only fire this event if there is campaign data
    if (hasTrackingData) {
      customEvent('initiate_tracking', {})
    }
  }, [hasTrackingData])

  useEffect(() => {
    if (!publicRuntimeConfig.FF_ENABLE_SNOWPLOW) return

    // Set OptanonWrapper to listen to consent changes
    // @see https://docs.snowplow.io/accelerators/consent/tracking/tracking_2/#using-onetrust
    // @see https://my.onetrust.com/s/article/UUID-518074a1-a6da-81c3-be52-bae7685d9c94?language=en_US
    window.OptanonWrapper = () => {
      window.OneTrust?.OnConsentChanged(() => {
        trackConsent()
      })
    }
  }, [])

  return (
    <Wrapper style={{ display: 'none' }}>
      <UniversalThirdPartyScripts
        dy={{
          siteId: publicRuntimeConfig.DY_SITE_ID,
          contextType: 'OTHER',
          cookieConsent,
        }}
        gtmId={publicRuntimeConfig.GTM_ID}
        hostUrl={publicRuntimeConfig.HOST_URL}
      />
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <HeaderFlagsContext.Provider value={headerFlags}>
            <Component {...pageProps} memberDetails={memberDetails} />
          </HeaderFlagsContext.Provider>
        </QueryClientProvider>
      </ThemeProvider>
    </Wrapper>
  )
}

MyApp.getInitialProps = async ({
  Component,
  ctx,
}: AppContext): Promise<InitialProps> => {
  const { utm_id, utm_campaign, utm_source, utm_medium, utm_term } = ctx.query
  const pageProps =
    Component.getInitialProps && (await Component.getInitialProps(ctx))

  const [{ data: memberDetails }, headerFlags] = await Promise.all([
    memberApi
      .getMemberDetails(
        {},
        { headers: { cookie: authCookies(ctx.req?.headers.cookie) } },
      )
      .catch((err: unknown) => {
        if (err instanceof Object && err !== null && 'message' in err) {
          console.info(
            `memberAPI.getMemberDetails throws Error: ${err.message}`,
          )
        }

        // Ignore errors. Some pages don't require logged-in state.
        return { data: null }
      }),
    getHeaderFlags(ctx),
  ])

  if (memberDetails && ctx?.req && ctx?.res) {
    // Set (necessary) user cookies
    setServerUserNapaCookies(
      ctx.req,
      ctx.res,
      publicRuntimeConfig.COOKIE_DOMAIN,
      {
        id: memberDetails.id,
        memberName: memberDetails.memberName,
      },
    )
  }

  return {
    ...pageProps,
    memberDetails: memberDetails || undefined,
    headerFlags,
    cookieConsent:
      ctx.req && ctx.res
        ? getServerCookieConsent(ctx.req, ctx.res).preferences
        : false,
    hasTrackingData:
      !!(utm_id || utm_source || utm_medium || utm_campaign || utm_term) ||
      false,
  }
}

export default MyApp
